import {ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {ButtonLink} from 'components/DSYS/Link';
import {ReactNode} from 'react';

import styles from './Banner.module.css';
import {bannerGradient, DEFAULT_LIGHT_BANNER_COLOR, isHexColor} from './bannerColors';
import {BannerView} from './BannerView';

/** What a managed banner shows. Both the admin and home APIs return these fields. */
export type ManagedBannerContent = {
  title: string;
  subtitle?: string | null;
  scrolling_strip?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  light_background_color?: string | null;
  dark_background_color?: string | null;
};

/**
 * Renders a managed banner, on home and in the admin preview.
 *
 * In dark mode it uses the dark color, with light text, and the dark image when
 * there is one. A banner without a dark color keeps its light look on a dark
 * page, the way the calendar-driven banners do.
 */
export const ManagedBanner = ({
  banner,
  mode,
  imageSrc,
  darkImageSrc,
  imagePlaceholder,
  onDismiss,
  onCtaClick,
}: {
  banner: ManagedBannerContent;
  mode: 'bright' | 'dark';
  imageSrc?: string;
  darkImageSrc?: string;
  /** Shown in the image card when there's no image, e.g. in the admin preview. */
  imagePlaceholder?: ReactNode;
  onDismiss?: () => void;
  onCtaClick?: () => void;
}) => {
  // The admin preview can hold a color the admin is still typing; skip it.
  const usable = (color?: string | null) => (color && isHexColor(color) ? color : undefined);
  const darkColor = usable(banner.dark_background_color);
  const isDark = mode === 'dark' && Boolean(darkColor);
  const color = isDark
    ? darkColor!
    : (usable(banner.light_background_color) ?? DEFAULT_LIGHT_BANNER_COLOR);
  const image = isDark ? (darkImageSrc ?? imageSrc) : imageSrc;
  const hasCta = Boolean(banner.cta_label && banner.cta_url);

  return (
    <ThemeProvider mode={isDark ? 'dark' : 'bright'}>
      <ThemeContainer>
        <BannerView
          followsTheme
          title={banner.title}
          subtitle={
            (banner.subtitle || hasCta) && (
              <>
                {banner.subtitle}
                {banner.subtitle && hasCta && ' '}
                {hasCta && (
                  <ButtonLink
                    className={styles.buttonLink}
                    variant="transparent"
                    to={banner.cta_url!}
                    onClick={onCtaClick}
                  >
                    {banner.cta_label}
                  </ButtonLink>
                )}
              </>
            )
          }
          marquee={banner.scrolling_strip ? {text: banner.scrolling_strip} : undefined}
          background={bannerGradient(color)}
          image={image}
          imageNode={image ? undefined : imagePlaceholder}
          coverImage
          onDismiss={onDismiss}
        />
      </ThemeContainer>
    </ThemeProvider>
  );
};
